import { useCallback, useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import {
    ASPECT_RATIO,
    PROMO_PERCENT_WIDTH,
    STREAM_HEADER_HEIGHT,
} from '../../constants';

interface Output {
    initRefForContainer: (e: HTMLElement | null) => void;
    heightStreamContainer: number;
    widthStreamPlayer: number;
}

const useStreamPlayerBreakpoints = (): Output => {
    const widthObserver = useRef<typeof ResizeObserver.prototype>();
    const observedNode = useRef<Element>();

    const [clientSize, setClientSize] = useState<[number, number]>([0, 0]);

    const [clientWidth] = clientSize;

    const handleResize = (entries: Array<{ target: Element | undefined }>) => {
        const entry = entries[0]?.target;

        if (!entry) return;

        setClientSize([entry.clientWidth, entry.clientHeight]);
    };

    useEffect(() => {
        if (!widthObserver.current) return;

        handleResize([{ target: observedNode.current }]);
    }, []);

    const initObserver = useCallback(() => {
        widthObserver.current = new ResizeObserver(handleResize);

        if (observedNode.current) {
            widthObserver.current.observe(observedNode.current);
        }
    }, []);

    const destroyObserver = useCallback(() => {
        if (widthObserver.current) {
            widthObserver.current.unobserve(observedNode.current);
            observedNode.current = undefined;
        }
    }, []);

    const widthStreamPlayer = clientWidth
        ? clientWidth - clientWidth * PROMO_PERCENT_WIDTH
        : 0;

    const heightStreamContainer =
        ASPECT_RATIO * widthStreamPlayer + STREAM_HEADER_HEIGHT;

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--stream-height',
            `${heightStreamContainer}px`
        );
    }, [heightStreamContainer]);

    const initRefForContainer = useCallback(
        node => {
            if (!node && widthObserver.current) {
                destroyObserver();
            }

            observedNode.current = node;

            initObserver();
        },
        [initObserver, destroyObserver]
    );

    return {
        initRefForContainer,
        widthStreamPlayer,
        heightStreamContainer,
    };
};

export default useStreamPlayerBreakpoints;
